import { useState, useMemo } from 'react';
import { PERM_KEYS } from '../../config/styles';

// ════════════════════════════════════════════════════════════
// StaffManager — إدارة الموظفين والأدوار الاحترافية.
//   نفس design system العملاء (Data Grid + بحث/فلاتر + كروت أدوار).
//   بيحافظ على نفس الـ handlers/modals الموجودة (مش بيغيّر المنطق).
//   props:
//     employees, roles, lang, isRTL, t
//     onAddEmp, onEditEmp(emp), onDeleteEmp(id), onToggleEmp(emp)
//     onAddRole, onEditRole(role), onDeleteRole(id)
// ════════════════════════════════════════════════════════════

const P1 = '#667eea', P2 = '#764ba2';
const G1 = `linear-gradient(135deg,${P1} 0%,${P2} 100%)`;
const NAVY = 'linear-gradient(135deg,#1e3a5f 0%,#2d5a8c 100%)';

export default function StaffManager({
  employees = [], roles = [], lang = 'ar', isRTL = true, t = {},
  onAddEmp, onEditEmp, onDeleteEmp, onToggleEmp,
  onAddRole, onEditRole, onDeleteRole,
}) {
  const [q, setQ]             = useState('');
  const [fRole, setFRole]     = useState('');
  const [fStatus, setFStatus] = useState('');

  const roleLabel = (r) => r ? (lang === 'ar' ? (r.label_ar || r.name) : (r.label_en || r.name)) : '—';
  const empName   = (e) => lang === 'ar' ? (e.name_ar || e.name_en || '؟') : (e.name_en || e.name_ar || '?');
  const permCount = (row) => PERM_KEYS.filter(k => row[k]).length;

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return employees.filter(e => {
      const hay = [e.name_ar, e.name_en, e.username].filter(Boolean).join(' ').toLowerCase();
      const hit = !term || hay.includes(term);
      const rOk = !fRole || String(e.role_id) === String(fRole);
      const sOk = !fStatus || (fStatus === 'on' ? e.is_active !== false : e.is_active === false);
      return hit && rOk && sOk;
    });
  }, [employees, q, fRole, fStatus]); // eslint-disable-line

  const S = {
    head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
    title: { fontSize: 17, fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8, margin: 0 },
    addEmp: { background: NAVY, color: '#fff', border: 'none', padding: '11px 20px', borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 18px rgba(30,58,95,.3)' },
    addRole: { background: G1, color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 11, fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(102,126,234,.3)' },
    bar: { background: '#fff', borderRadius: 14, padding: 14, marginBottom: 14, boxShadow: '0 2px 12px rgba(0,0,0,.04)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
    search: { flex: 1, minWidth: 220, border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 14, outline: 'none', fontFamily: 'inherit' },
    sel: { border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 13, background: '#fff', cursor: 'pointer', outline: 'none', fontWeight: 600, color: '#1e293b', fontFamily: 'inherit' },
    count: { fontSize: 12.5, color: '#64748b', fontWeight: 700, padding: '0 4px' },
    grid: { background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,.04)' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { background: '#f8fafc', padding: '13px 14px', fontSize: 12.5, fontWeight: 700, color: '#64748b', textAlign: isRTL ? 'right' : 'left', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' },
    td: { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 13.5, verticalAlign: 'middle' },
    un: { fontFamily: 'monospace', background: '#f1f5f9', padding: '3px 8px', borderRadius: 6, fontSize: 12, color: P2, fontWeight: 700 },
    mini: (bg, c) => ({ background: bg, color: c, border: 'none', borderRadius: 8, padding: '6px 9px', fontSize: 12, cursor: 'pointer', fontWeight: 700 }),
  };
  const badge = (on) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: on ? '#d1fae5' : '#fee2e2', color: on ? '#047857' : '#b91c1c' });
  const roleBadge = (name) => {
    const map = { admin: ['#fef3c7', '#92400e'], manager: ['#ede9fe', P2] };
    const [bg, c] = map[name] || ['#e0f2fe', '#0369a1'];
    return { background: bg, color: c, padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700 };
  };

  return (
    <div>
      {/* ═══ الموظفين ═══ */}
      <div style={{ marginBottom: 30 }}>
        <div style={S.head}>
          <h3 style={S.title}>👨‍💼 {t.manageEmployees || 'الموظفين'}</h3>
          <button style={S.addEmp} onClick={onAddEmp}>＋ {t.addEmployee || 'إضافة موظف'}</button>
        </div>

        <div style={S.bar}>
          <input style={S.search} placeholder="ابحث باسم الموظف أو اليوزرنيم…" value={q} onChange={e => setQ(e.target.value)} />
          <select style={S.sel} value={fRole} onChange={e => setFRole(e.target.value)}>
            <option value="">كل الأدوار</option>
            {roles.map(r => <option key={r.id} value={r.id}>{roleLabel(r)}</option>)}
          </select>
          <select style={S.sel} value={fStatus} onChange={e => setFStatus(e.target.value)}>
            <option value="">كل الحالات</option>
            <option value="on">نشط فقط</option>
            <option value="off">موقوف فقط</option>
          </select>
          <span style={S.count}>{list.length} من {employees.length} موظف</span>
        </div>

        <div style={S.grid}>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: 40 }}>#</th>
                  <th style={S.th}>الموظف</th>
                  <th style={S.th}>اليوزرنيم</th>
                  <th style={S.th}>الدور</th>
                  <th style={S.th}>الصلاحيات</th>
                  <th style={S.th}>الحالة</th>
                  <th style={{ ...S.th, width: 130 }}>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {list.map((e, i) => {
                  const on = e.is_active !== false;
                  return (
                    <tr key={e.id} style={{ transition: '.15s' }}
                        onMouseEnter={ev => ev.currentTarget.style.background = '#faf5ff'}
                        onMouseLeave={ev => ev.currentTarget.style.background = ''}>
                      <td style={{ ...S.td, color: '#94a3b8', fontWeight: 700 }}>{i + 1}</td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 36, height: 36, borderRadius: 10, background: on ? NAVY : '#e5e7eb', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 15, fontWeight: 800, flexShrink: 0 }}>{empName(e).charAt(0)}</div>
                          <span style={{ fontWeight: 700, color: '#1e293b' }}>{empName(e)}</span>
                        </div>
                      </td>
                      <td style={S.td}><span style={S.un}>@{e.username}</span></td>
                      <td style={S.td}><span style={roleBadge(e.roles?.name)}>{roleLabel(e.roles)}</span></td>
                      <td style={S.td}><span style={{ background: '#eef2ff', color: P1, padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700 }}>{permCount(e)} / {PERM_KEYS.length} صلاحية</span></td>
                      <td style={S.td}><span style={badge(on)}><span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />{on ? 'نشط' : 'موقوف'}</span></td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button title={on ? 'إيقاف' : 'تفعيل'} style={S.mini('#fff7ed', '#c2410c')} onClick={() => onToggleEmp(e)}>{on ? '⏸️' : '▶️'}</button>
                          <button title="تعديل" style={S.mini('#ede9fe', P2)} onClick={() => onEditEmp(e)}>✏️</button>
                          <button title="حذف" style={S.mini('#fee2e2', '#b91c1c')} onClick={() => onDeleteEmp(e.id)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!list.length && (
                  <tr><td colSpan="7" style={{ padding: 36, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>لا يوجد موظفين مطابقين</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ═══ الأدوار الوظيفية ═══ */}
      <div>
        <div style={S.head}>
          <h3 style={S.title}>🏷️ {t.manageRoles || 'الأدوار الوظيفية'}</h3>
          <button style={S.addRole} onClick={onAddRole}>＋ {t.addRole || 'إضافة دور'}</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 14 }}>
          {roles.map(role => (
            <div key={role.id} style={{ background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 2px 12px rgba(0,0,0,.04)', border: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={roleBadge(role.name)}>{roleLabel(role)}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>@{role.name}</span>
                  </div>
                  <span style={{ fontSize: 11.5, color: P1, background: '#eef2ff', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>{employees.filter(e => e.role_id === role.id).length} موظف</span>
                </div>
                <div style={{ display: 'flex', gap: 5 }}>
                  <button title="تعديل" style={S.mini('#ede9fe', P2)} onClick={() => onEditRole(role)}>✏️</button>
                  <button title="حذف" style={S.mini('#fee2e2', '#b91c1c')} onClick={() => onDeleteRole(role.id)}>🗑️</button>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {PERM_KEYS.map(k => (
                  <span key={k} style={{ fontSize: 10.5, padding: '3px 7px', borderRadius: 6, fontWeight: 600, background: role[k] ? '#d1fae5' : '#f3f4f6', color: role[k] ? '#059669' : '#9ca3af' }}>
                    {role[k] ? '✓' : '✗'} {(t.permLabels && t.permLabels[k]) || k}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {!roles.length && (
            <div style={{ gridColumn: '1/-1', padding: 36, textAlign: 'center', color: '#94a3b8', background: '#fff', borderRadius: 14, fontSize: 14 }}>لا توجد أدوار بعد</div>
          )}
        </div>
      </div>
    </div>
  );
}
